// Tumaini 2.0 entry point — boot sequence.
// Order matters: error handlers first (nothing may fail unobserved), then the
// action router, then the db client, then the auth gate: login view OR
// shell + route. Ported from the legacy DOMContentLoaded handler (~1117).

import { installGlobalHandlers } from './errors.js';
import { installActions, register } from './ui/actions.js';
import { closeModal } from './ui/modal.js';
import { store, SLICES } from './state/store.js';
import { esc } from './ui/esc.js';
import { initClient, getClient, SUPABASE_URL, SUPABASE_ANON_KEY } from './db/client.js';
import { isAdmin, startIdleTimer, logoutApp } from './auth.js';
import { readMode, resolveMode, storageAvailable } from './mode.js';
import { showLogin, showLoginErr, startAccess } from './views/login.js';
import { mountShell } from './views/shell.js';
import { initProgrammes } from './programmes.js';
import { logSignIn } from './data/logins.js';
import { setupRealtime } from './realtime.js';
import { initHelp } from './views/help.js';
import { registerToday } from './views/today.js';
import { registerBoard } from './views/board.js';
import { registerPatients } from './views/patients.js';
import { registerLegacy } from './views/legacy.js';
import { registerConsents } from './views/consents.js';
import { registerSafety } from './views/safety.js';
import { registerWorkspace } from './views/workspace.js';
import { registerImport } from './views/import.js';
import { registerTraining } from './views/training.js';
import { registerReports } from './views/reports.js';
import { registerBilling } from './views/billing.js';
import { registerTeam } from './views/team.js';
import { registerAdmin } from './views/admin.js';
import {
  ROUTES,
  registerView,
  getView,
  routeFor,
  startRouter,
  navigate,
  syncHash,
} from './views/registry.js';

// 1. Global error handlers — before anything else can throw.
installGlobalHandlers();

// 2. Actions — ONE delegated router (no inline onclick anywhere).
installActions(document);
register('nav', (el) => {
  const view = el.getAttribute('data-view');
  if (view) navigate(view);
});
register('logout', () => {
  logoutApp(); // guaranteed sb-* teardown even when signOut network-fails
});
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') closeModal(); // idempotent when shut (legacy parity)
});

// 3. Placeholder views — one per route, replaced by real views as Phase 2+ land.
for (const r of ROUTES) {
  registerView(r.id, {
    render(host) {
      host.innerHTML =
        `<header class="view-head"><h1>${esc(r.title)}</h1></header>` +
        `<p class="view-placeholder">The ${esc(r.title)} view arrives in Phase 2.</p>`;
    },
  });
}

// Real views (override the placeholder above). Today is the Phase-2 landing.
registerToday();
registerBoard(); // Patient board — stage pipeline (Phase 4 Task A)
registerPatients(); // Patients registry + bulk assign + CSV (Phase 4 Task B)
registerLegacy(); // Legacy patients — 2024-26 register migration sub-queues
registerConsents(); // New consents (intake) + promote handshake (Phase 3 Task A)
registerSafety(); // Adverse-events register (Task C)
registerWorkspace(); // patient workspace overlay actions (Task C; opened over any view)
registerImport(); // Bulk import (Phase 3 Task B) — two-mode xlsx upload + preview
registerTraining(); // Training course — player, watch analytics, after-video checks
registerReports(); // Reports — METRICS registry + full-fetch snapshot (Phase 5 Task A)
registerBilling(); // Billing & expenses + the R-023 sponsor statement (Phase 5 Task B)
registerTeam(); // Team activity — sign-in audit + training progress (MANAGE only)
registerAdmin(); // Admin console — team/roles, R-078 offboarding, audit, error log (Phase 6 Task A)

// 4. Route — render on every hash change; the shell re-renders its chrome
// via its UI-slice subscription (fired by the store.set below).
function onRoute(id) {
  const ui = store.get(SLICES.UI) || {};
  const resolved = routeFor('#/' + id, ui);
  // Keep the address bar truthful when the gate bounced the request —
  // otherwise a later navigate() to the still-displayed hash is a silent
  // no-op (identical hash assignment fires no hashchange).
  syncHash(resolved);
  store.set(SLICES.UI, { view: resolved });
  const host = document.getElementById('view');
  if (!host) return;
  const view = getView(resolved);
  if (view) view.render(host);
  if (view && view.load) view.load();
  // The nav rebuild just destroyed the button the keyboard user activated
  // (focus fell to <body>). Land focus on the freshly rendered view — the
  // tabindex="-1" host exists for exactly this (WCAG 2.4.3); its h1 is the
  // next thing a screen reader announces.
  if (typeof (/** @type {any} */ (host).focus) === 'function') {
    /** @type {any} */ (host).focus();
  }
}

// 5. Post-gate entry: role-aware UI defaults, shell, router, idle countdown.
// Runs ONCE per page load (review R-11). login.js already latches onSuccess;
// this second guard keeps the invariant local to the code that would break —
// a second call would mount the shell again and bind a second hashchange
// listener. Sign-out reloads the page, which is what re-arms it.
let _entered = false;
function enterApp() {
  if (_entered) return;
  _entered = true;
  const sess = store.get(SLICES.SESSION) || {};
  const admin = isAdmin(sess.role);
  // UI mode: a stored choice wins (legacy keys, shared with v_20 on purpose —
  // see src/mode.js); otherwise the role default applies. R-030: readMode()
  // already returns null instead of throwing when storage is unavailable, so
  // the role default takes over silently — what was missing was TELLING the
  // user, which storageAvailable() + the chip caveat now do.
  const mode = resolveMode(readMode(sess.user && sess.user.id), sess.role);
  store.set(SLICES.UI, { mode, modePersisted: storageAvailable(), isAdmin: admin, view: null });
  mountShell();
  startRouter(onRoute);
  // Programme pill + catalogue load (async; the pill appears when it lands —
  // legacy showed the switcher only once PROGRAMMES was non-empty).
  void initProgrammes();
  setupRealtime(); // Live strip + coalesced change notifications
  initHelp(); // Help modal entry points (top bar + sidebar footer)
  startIdleTimer(); // begin inactivity auto-logout now that a staff user is signed in
}

// 6. Boot: db client → login screen → session restore (F-AUTH-02).
async function boot() {
  const host = document.getElementById('app');
  if (!host) return;
  /** @type {any} */
  let client = null;
  try {
    client = initClient(SUPABASE_URL, SUPABASE_ANON_KEY);
  } catch {
    /* vendor script missing — surfaced below (legacy line 1118) */
  }
  // Sign-in audit (F-ADMIN §5): every successful entry writes one app_logins
  // row — 'login' when credentials were typed, 'restore' when an existing
  // session resumed on page load. Best-effort; never blocks entry.
  let restored = false;
  showLogin(host, {
    onSuccess: () => {
      enterApp();
      const sess = store.get(SLICES.SESSION) || {};
      void logSignIn(restored ? 'restore' : 'login', sess.user || null);
    },
  });
  if (!client) {
    showLoginErr('Supabase unavailable');
    return;
  }
  const { data } = await getClient().auth.getSession();
  const session = data && data.session;
  // Restore without re-login — through startAccess so a restored aal1 session
  // on an MFA-enrolled account is challenged exactly like a fresh sign-in
  // (R-021); an account with no factor is unaffected.
  if (session) {
    restored = true;
    await startAccess(session.user);
  }
}

boot();
